import { Redirect, useHistory, useLocation } from "react-router";
import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import ActivityCollection from "./ActivityCollection";
import Cookies from "js-cookie";
import { useTranslation } from "react-i18next";
import PageHeader from "../../components/PageHeader";
import { UI_CONSTANTS } from "../../constants/ui";
import PageHeaderExportButton, {
  getPageHeaderExportMenuItems,
} from "../../ui/components/PageHeaderExportButton";
import {
  PeriodFilterModal,
  SingleSelectFilterModal,
  TableFilterBar,
  TableFilterButton,
  TableFilterClearLink,
  hasActiveClearableFilters,
} from "../../ui/components/filters";
import { useActivityFilters } from "./hooks/useActivityFilters";
import { useActivityPageFilterOptions } from "./hooks/useActivityPageFilterOptions";
import { useSuperContainers } from "./hooks/useSuperContainers";
import {
  buildHistoricalOriginFilterOptions,
  buildKeywordFilterValue,
  getFilterLabel,
  syncKeywordFiltersFromValue,
  withHistoricalFluxOptions,
} from "./utils/activityPageFilters";
import ActivityWhatsNewBanner from "./components/ActivityWhatsNewBanner";
import HowItWorksButton from "../../components/howItWorks/HowItWorksButton";
import { getStatusOptions } from "../characterization-2/characterizationList/StatusFilterModal";

const Activity = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const token = Cookies.get("userToken");

  const {
    keywordFilter,
    setKeywordFilter,
    periodHistoryStart,
    setPeriodHistoryStart,
    periodHistoryStop,
    setPeriodHistoryStop,
    periodFilter,
    setPeriodFilter,
    periodName,
    setPeriodName,
    originFilter,
    setOriginFilter,
    characterizationFilter,
    setCharacterizationFilter,
    characterizationStatusFilter,
    setCharacterizationStatusFilter,
  } = useActivityFilters();

  const { groupedOptions: mongoGroupedOptions } =
    useActivityPageFilterOptions();
  const { superContainers } = useSuperContainers();

  const [isPeriodModalOpen, setIsPeriodModalOpen] = useState(false);
  const [isFluxModalOpen, setIsFluxModalOpen] = useState(false);
  const [isAddressModalOpen, setIsAddressModalOpen] = useState(false);
  const [isZoneModalOpen, setIsZoneModalOpen] = useState(false);
  const [isOriginModalOpen, setIsOriginModalOpen] = useState(false);
  const [isCharacterizationModalOpen, setIsCharacterizationModalOpen] =
    useState(false);
  const [
    isCharacterizationStatusModalOpen,
    setIsCharacterizationStatusModalOpen,
  ] = useState(false);
  const [handleValidate, setHandleValidate] = useState(0);
  const [zoneFilter, setZoneFilter] = useState("all");
  const [fluxFilter, setFluxFilter] = useState("all");
  const [addressFilter, setAddressFilter] = useState("all");
  const [historicalFluxLabels, setHistoricalFluxLabels] = useState([]);
  const [historicalOriginLabels, setHistoricalOriginLabels] = useState([]);
  const [exportState, setExportState] = useState({
    disabled: true,
    items: [],
  });

  const hasInitializedKeywordFilters = useRef(false);

  const handleExportStateChange = useCallback((nextExportState) => {
    setExportState(nextExportState);
  }, []);

  const handleHistoricalFluxLabels = useCallback((labels) => {
    setHistoricalFluxLabels(Array.isArray(labels) ? labels : []);
  }, []);

  const handleHistoricalOriginLabels = useCallback((labels) => {
    setHistoricalOriginLabels(Array.isArray(labels) ? labels : []);
  }, []);

  const groupedOptions = useMemo(
    () => withHistoricalFluxOptions(mongoGroupedOptions, historicalFluxLabels),
    [mongoGroupedOptions, historicalFluxLabels],
  );

  const exportMenuItems = useMemo(
    () => getPageHeaderExportMenuItems(exportState.items),
    [exportState.items],
  );

  const withAllOption = useCallback(
    (options) => [{ label: t("All"), value: "all" }, ...options],
    [t],
  );

  const zoneOptions = useMemo(
    () => withAllOption(groupedOptions.zone),
    [groupedOptions.zone, withAllOption],
  );
  const fluxOptions = useMemo(
    () => withAllOption(groupedOptions.flux),
    [groupedOptions.flux, withAllOption],
  );
  const addressOptions = useMemo(
    () => withAllOption(groupedOptions.address),
    [groupedOptions.address, withAllOption],
  );
  const originOptions = useMemo(() => {
    const labels = [...historicalOriginLabels];
    if (originFilter && originFilter !== "all") {
      labels.push(originFilter);
    }
    return withAllOption(buildHistoricalOriginFilterOptions(labels));
  }, [historicalOriginLabels, originFilter, withAllOption]);

  // Same corporationModules pattern as AppSidebar / DashboardIndicators.
  const characterizationModuleOn = useMemo(() => {
    try {
      const modules = JSON.parse(
        localStorage.getItem("corporationModules") || "null",
      );
      return modules ? Boolean(modules.showCharacterizationModule) : true;
    } catch {
      return true;
    }
  }, []);

  // Level 1 = history not fully characterized (not_sent).
  // Level 2 = complete characterization (sent).
  const characterizationOptions = useMemo(
    () =>
      withAllOption([
        { label: t("Level 1 characterization"), value: "not_sent" },
        { label: t("Level 2 characterization"), value: "sent" },
      ]),
    [withAllOption, t],
  );

  const characterizationModalOptions = useMemo(
    () => [
      { label: t("All"), value: "all" },
      { label: t("Level 1 characterization"), value: "not_sent" },
      { label: t("Level 2 characterization"), value: "sent" },
    ],
    [t],
  );

  // History cycles only know these three statuses (no correction/error state).
  const characterizationStatusOptions = useMemo(
    () =>
      getStatusOptions(t).filter((option) =>
        ["all", "ImageEnTraitement", "ACompleter", "Validated"].includes(
          option.value,
        ),
      ),
    [t],
  );

  // Keep the characterization filters in the URL so the view is shareable
  // (e.g. a link sent by CS) and reachable from Dashboard / Characterization.
  useEffect(() => {
    const params = new URLSearchParams(location.search || "");
    const nextParams = new URLSearchParams(params);
    if (characterizationModuleOn && characterizationFilter !== "all") {
      nextParams.set("characterization", characterizationFilter);
    } else {
      nextParams.delete("characterization");
    }
    if (
      characterizationModuleOn &&
      characterizationFilter === "sent" &&
      characterizationStatusFilter !== "all"
    ) {
      nextParams.set("characterizationStatus", characterizationStatusFilter);
    } else {
      nextParams.delete("characterizationStatus");
    }
    const currentSearch = params.toString();
    const nextSearch = nextParams.toString();
    if (nextSearch !== currentSearch) {
      history.replace({
        pathname: location.pathname,
        search: nextSearch ? `?${nextSearch}` : "",
      });
    }
  }, [
    characterizationModuleOn,
    characterizationFilter,
    characterizationStatusFilter,
    location.pathname,
    location.search,
    history,
  ]);

  useEffect(() => {
    if (hasInitializedKeywordFilters.current || !keywordFilter?.value?.length) {
      return;
    }

    const synced = syncKeywordFiltersFromValue(keywordFilter);
    setZoneFilter(synced.zoneFilter);
    setFluxFilter(synced.fluxFilter);
    setAddressFilter(synced.addressFilter);
    hasInitializedKeywordFilters.current = true;
  }, [keywordFilter]);

  const applyKeywordFilters = useCallback(
    (patch) => {
      const next = {
        zoneFilter,
        fluxFilter,
        addressFilter,
        ...patch,
      };

      if (patch.zoneFilter !== undefined) setZoneFilter(patch.zoneFilter);
      if (patch.fluxFilter !== undefined) setFluxFilter(patch.fluxFilter);
      if (patch.addressFilter !== undefined) {
        setAddressFilter(patch.addressFilter);
      }

      setKeywordFilter(
        buildKeywordFilterValue({
          ...next,
          optionsByValue: groupedOptions.byValue,
        }),
      );
      setHandleValidate((previous) => previous + 1);
    },
    [
      zoneFilter,
      fluxFilter,
      addressFilter,
      groupedOptions.byValue,
      setKeywordFilter,
    ],
  );

  const handleClearFilters = useCallback(() => {
    setZoneFilter("all");
    setFluxFilter("all");
    setAddressFilter("all");
    setOriginFilter("all");
    setCharacterizationFilter("all");
    setCharacterizationStatusFilter("all");
    setKeywordFilter({ value: [] });
    setHandleValidate((previous) => previous + 1);
  }, [
    setKeywordFilter,
    setOriginFilter,
    setCharacterizationFilter,
    setCharacterizationStatusFilter,
  ]);

  const showClearFilters = hasActiveClearableFilters([
    { value: fluxFilter, defaultValue: "all" },
    { value: addressFilter, defaultValue: "all" },
    { value: zoneFilter, defaultValue: "all" },
    { value: originFilter, defaultValue: "all" },
    { value: characterizationFilter, defaultValue: "all" },
    { value: characterizationStatusFilter, defaultValue: "all" },
  ]);
  if (token) {
    return (
      <div className="settings-page-wrapper">
        <PageHeader
          title={t("Historique")}
          tabs={[]}
          helpButton={<HowItWorksButton topic="history" />}
          actions={
            <PageHeaderExportButton
              disabled={exportState.disabled}
              items={exportState.items}
            />
          }
          actionsMenuItems={exportMenuItems}
        />
        <ActivityWhatsNewBanner />
        <div
          className="settings-content-wrapper activity-page-content"
          style={{
            maxWidth: UI_CONSTANTS.LAYOUT.ACTIVITY_PAGE_MAX_WIDTH,
            marginLeft: "auto",
            marginRight: "auto",
          }}
        >
          <TableFilterBar
            className="activity-page-filter-bar mt-2"
            filters={
              <>
                <TableFilterButton
                  label={t("Period")}
                  value={periodName || t("Last 6 months")}
                  onClick={() => setIsPeriodModalOpen(true)}
                />
                <TableFilterButton
                  label={t("Flux")}
                  value={getFilterLabel(fluxOptions, fluxFilter, t("All"))}
                  onClick={() => setIsFluxModalOpen(true)}
                />
                <TableFilterButton
                  label={t("Address")}
                  value={getFilterLabel(
                    addressOptions,
                    addressFilter,
                    t("All"),
                  )}
                  onClick={() => setIsAddressModalOpen(true)}
                />
                <TableFilterButton
                  label={t("settingsClients.filterZones")}
                  value={getFilterLabel(zoneOptions, zoneFilter, t("All"))}
                  onClick={() => setIsZoneModalOpen(true)}
                />
                <TableFilterButton
                  label={t("Waste origin")}
                  value={getFilterLabel(originOptions, originFilter, t("All"))}
                  onClick={() => setIsOriginModalOpen(true)}
                />
                {characterizationModuleOn && (
                  <TableFilterButton
                    label={t("Characterization level")}
                    value={getFilterLabel(
                      characterizationOptions,
                      characterizationFilter,
                      t("All"),
                    )}
                    onClick={() => setIsCharacterizationModalOpen(true)}
                  />
                )}
                {characterizationModuleOn &&
                  characterizationFilter === "sent" && (
                    <TableFilterButton
                      label={t("Status")}
                      value={getFilterLabel(
                        characterizationStatusOptions,
                        characterizationStatusFilter,
                        t("All"),
                      )}
                      onClick={() => setIsCharacterizationStatusModalOpen(true)}
                    />
                  )}
                <TableFilterClearLink
                  visible={showClearFilters}
                  onClear={handleClearFilters}
                />
              </>
            }
          />

          <PeriodFilterModal
            isOpen={isPeriodModalOpen}
            onClose={() => setIsPeriodModalOpen(false)}
            currentPeriodFilter={periodFilter}
            currentPeriodStart={periodHistoryStart}
            currentPeriodEnd={periodHistoryStop}
            onApply={(months, label, startDateIso, endDateIso, presetId) => {
              setPeriodFilter(presetId === "sinceBeginning" ? null : months);
              setPeriodName(label || t("Last 6 months"));
              if (startDateIso) setPeriodHistoryStart(startDateIso);
              if (endDateIso) setPeriodHistoryStop(endDateIso);
              setHandleValidate((previous) => previous + 1);
            }}
          />

          <SingleSelectFilterModal
            isOpen={isFluxModalOpen}
            onClose={() => setIsFluxModalOpen(false)}
            title={t("Flux")}
            options={fluxOptions}
            currentValue={fluxFilter}
            onApply={(value) => applyKeywordFilters({ fluxFilter: value })}
          />

          <SingleSelectFilterModal
            isOpen={isAddressModalOpen}
            onClose={() => setIsAddressModalOpen(false)}
            title={t("Address")}
            options={addressOptions}
            currentValue={addressFilter}
            onApply={(value) => applyKeywordFilters({ addressFilter: value })}
          />

          <SingleSelectFilterModal
            isOpen={isZoneModalOpen}
            onClose={() => setIsZoneModalOpen(false)}
            title={t("settingsClients.filterZones")}
            options={zoneOptions}
            currentValue={zoneFilter}
            onApply={(value) => applyKeywordFilters({ zoneFilter: value })}
          />

          <SingleSelectFilterModal
            isOpen={isOriginModalOpen}
            onClose={() => setIsOriginModalOpen(false)}
            title={t("Waste origin")}
            options={originOptions}
            currentValue={originFilter}
            onApply={(value) => {
              setOriginFilter(value || "all");
              setHandleValidate((previous) => previous + 1);
            }}
          />

          <SingleSelectFilterModal
            isOpen={isCharacterizationModalOpen}
            onClose={() => setIsCharacterizationModalOpen(false)}
            title={t("Characterization level")}
            options={characterizationModalOptions}
            currentValue={characterizationFilter}
            onApply={(value) => {
              setCharacterizationFilter(value || "all");
              if (value !== "sent") setCharacterizationStatusFilter("all");
              setHandleValidate((previous) => previous + 1);
            }}
          />

          <SingleSelectFilterModal
            isOpen={isCharacterizationStatusModalOpen}
            onClose={() => setIsCharacterizationStatusModalOpen(false)}
            title={t("Status")}
            options={characterizationStatusOptions}
            currentValue={characterizationStatusFilter}
            onApply={(value) => {
              setCharacterizationStatusFilter(value || "all");
              setHandleValidate((previous) => previous + 1);
            }}
          />

          <ActivityCollection
            keywordFilter={keywordFilter}
            periodHistoryStart={periodHistoryStart}
            periodHistoryStop={periodHistoryStop}
            superContainers={superContainers}
            handleValidate={handleValidate}
            onExportStateChange={handleExportStateChange}
            onHistoricalFluxLabels={handleHistoricalFluxLabels}
            originFilter={originFilter}
            onHistoricalOriginLabels={handleHistoricalOriginLabels}
            characterizationFilter={
              characterizationModuleOn ? characterizationFilter : "all"
            }
            characterizationStatusFilter={
              characterizationModuleOn ? characterizationStatusFilter : "all"
            }
            onClearFilters={handleClearFilters}
          />
        </div>
      </div>
    );
  }

  return (
    <Redirect
      to={{
        pathname: "/login",
      }}
    />
  );
};

export default Activity;
